import { useState, useEffect, useRef, useLayoutEffect } from 'react';

type DragModalOptions = {
  closeThreshold?: number;
  velocityThreshold?: number;
  minDragDistance?: number;
  closingAnimationDuration?: number;
  openingAnimationDuration?: number;
  onClose?: () => void;
  shouldShow?: boolean;
  preventBodyVerticalScroll?: boolean;
  closeModalDelay?: number;
}

type DragModalReturn = {
  // Drag handlers
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  
  // External control handlers
  closeModal: () => void;
  
  // Modal style properties
  currentTranslateY: number;
  modalDragTransition: string;
  
  // State flags
  isDragging: boolean;
  isOpening: boolean;
  isClosing: boolean;
  hasActuallyDragged: boolean;
  
  // Click handler helper
  shouldPreventClick: () => boolean;
}

export const useDragModal = (options: DragModalOptions = {}): DragModalReturn => {
  const {
    closeThreshold = 150,
    velocityThreshold = 1.5,
    minDragDistance = 20,
    closingAnimationDuration = 400,
    openingAnimationDuration = 800,
    onClose,
    shouldShow = true,
    preventBodyVerticalScroll = false,
    closeModalDelay = 120,
  } = options;

  // Drag functionality states
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartY, setDragStartY] = useState<number>(0);
  const [currentTranslateY, setCurrentTranslateY] = useState<number>(window.innerHeight);
  const [dragVelocity, setDragVelocity] = useState<number>(0);
  const [lastDragTime, setLastDragTime] = useState<number>(0);
  const [lastDragY, setLastDragY] = useState<number>(0);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isOpening, setIsOpening] = useState<boolean>(true);
  const [hasActuallyDragged, setHasActuallyDragged] = useState<boolean>(false);
  const [maxDragDistance, setMaxDragDistance] = useState<number>(0);
  const isFirstRender = useRef<boolean>(true);


  // Handle opening animation - slide from bottom
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setCurrentTranslateY(window.innerHeight);
    }
    
    
    if (!shouldShow) return;
    
    // Reset states when modal is shown
    setIsClosing(false);
    setIsDragging(false);
    setIsOpening(true);
    
    // 1) Set the initial off-screen position
    const startY = window.innerHeight;
    setCurrentTranslateY(startY);
  
    // 2) Next frame: animate to 0
    const id = requestAnimationFrame(() => {
      setCurrentTranslateY(0);
    });

    // 3) Set isOpening to false after the opening animation completes
    const openingTimer = setTimeout(() => {
      setIsOpening(false);
    }, openingAnimationDuration);
  
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(openingTimer);
    };
  }, [shouldShow, openingAnimationDuration]);

  // Handle body scroll prevention when modal is open
  useEffect(() => {
    if (!preventBodyVerticalScroll) return;

    const isModalOpen = currentTranslateY === 0 && shouldShow && !isClosing;
    
    if (isModalOpen) {
      // Store the original overflow-y value
      const originalOverflowY = document.body.style.overflowY;
      document.body.style.overflowY = 'hidden';
      
      // Cleanup function to restore original overflow-y
      return () => {
        document.body.style.overflowY = originalOverflowY;
      };
    }
  }, [preventBodyVerticalScroll, currentTranslateY, shouldShow, isClosing]);

  // Drag functionality handlers
  const handleDragStart = (clientY: number) => {
    setIsDragging(true);
    setDragStartY(clientY);
    setLastDragTime(Date.now());
    setLastDragY(clientY);
    setDragVelocity(0);
    setHasActuallyDragged(false);
    setMaxDragDistance(currentTranslateY);
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return;

    const deltaY = clientY - dragStartY;
    const now = Date.now();
    const timeDelta = now - lastDragTime;
    
    // Mark that user has actually dragged (moved more than a few pixels)
    if (Math.abs(deltaY) > 5) {
      setHasActuallyDragged(true);
    }
    
    // Calculate velocity based on incremental movement, not total distance
    if (timeDelta > 0) {
      const incrementalDistance = clientY - lastDragY;
      const newVelocity = incrementalDistance / timeDelta;
      setDragVelocity(newVelocity);
    }
    
    setLastDragTime(now);
    setLastDragY(clientY);
    
    // Allow both downward and upward movement for better direction change tolerance
    if (deltaY > 0) {
      // Dragging down - move modal down
      setCurrentTranslateY(deltaY);
      setMaxDragDistance(Math.max(maxDragDistance, deltaY));
    } else {
      // Dragging up - allow modal to return to original position
      const newTranslateY = Math.max(0, currentTranslateY + (clientY - lastDragY));
      setCurrentTranslateY(newTranslateY);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Consider both current position and maximum distance reached during the drag
    const draggedEnoughAtAnyPoint = maxDragDistance > minDragDistance;
    const currentlyDraggedEnough = currentTranslateY > minDragDistance;
    
    // Consider the direction of the final velocity
    const isMovingUpward = dragVelocity < -0.3;
    const isMovingDownwardFast = dragVelocity > velocityThreshold;
    
    // User showed clear intent to close if they dragged far down at any point
    const showedCloseIntent = maxDragDistance > closeThreshold;
    
    // Determine if modal should close
    const shouldClose = draggedEnoughAtAnyPoint && !isMovingUpward && (
      currentTranslateY > closeThreshold || 
      (showedCloseIntent && isMovingDownwardFast) ||
      (isMovingDownwardFast && currentlyDraggedEnough)
    );
    
    if (shouldClose) {
      // Start smooth closing animation
      setIsClosing(true);
      
      // Calculate the target position - slide down to complete close
      const modalHeight = window.innerHeight;
      const targetY = Math.max(modalHeight, currentTranslateY + (Math.max(0, dragVelocity) * 200));
      setCurrentTranslateY(targetY);
      
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, closingAnimationDuration);
    } else {
      // Snap back to original position
      setCurrentTranslateY(0);
    }
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers (for desktop testing)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStartY, lastDragTime]);

  // Helper function to determine if click should be prevented
  const shouldPreventClick = () => {
    return hasActuallyDragged || isClosing || isOpening;
  };

  // External handler to close modal gracefully
  const closeModal = () => {
    if (isClosing || isDragging) return; // Prevent multiple close attempts or closing while dragging
    
    // Add a small delay before starting the closing animation for a more graceful feel
    setTimeout(() => {
      // Start smooth closing animation
      setIsClosing(true);
      
      // Calculate the target position - slide down to complete close
      const modalHeight = window.innerHeight;
      const targetY = modalHeight;
      setCurrentTranslateY(targetY);
      
      // Call onClose after animation completes
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, closingAnimationDuration + 130);
    }, closeModalDelay); // 250ms initial delay
  };

  // Generate modal drag transition string
  const modalDragTransition = isDragging ? 'none' : 
                    isClosing ? `transform ${closingAnimationDuration}ms ease-in` :
                    isOpening ? `transform ${openingAnimationDuration}ms ease-out` :
                    'transform 300ms ease-out';

  return {
    // Handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    
    
    // External control handlers
    closeModal,

    // Style properties
    currentTranslateY,
    modalDragTransition,
    
    // State flags
    isDragging,
    isOpening,
    isClosing,
    hasActuallyDragged,
    
    // Helpers
    shouldPreventClick
  };
};
